import {action, observable, toJS} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import React from 'react';
import Carousel from 'react-bootstrap/Carousel';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowUpRightFromSquare} from '@fortawesome/free-solid-svg-icons';

import {STATE_FULFILLED, STATE_PENDING, STATE_PRE} from '../../../../../constants/asyncConstants';
import LoadingIcon from '../../../../common/loadingIcon/LoadingIcon';
import inject from '../../../../hoc/injectHoc';
import pcLogo from '../../../../../assets/images/logo.png';

import './campaignBanner.scss';

const DEFAULT_INTERVAL_IN_MILLISECONDS = 10000;

/**
 * The CampaignBanner component.
 */
class CampaignBanner extends React.Component {
  videoRefs = [];

  isHoveredRef = false;
  pendingAdvanceRef = false;
  imageTimer = null;
  prevIsLoading = true;
  prevActiveIndex = 0;
  prevBannersLength = 0;

  @observable activeIndex = 0;

  /**
   * Triggered when the component is first mounted to the page.
   */
  componentDidMount() {
    const {apiTargetedCampaignGetAllStore, apiUserGetMeStore} = this.props;
    apiUserGetMeStore.getPromise().then((user) => {
      const planId = user && user.plan && user.plan.id ? String(user.plan.id) : undefined;
      apiTargetedCampaignGetAllStore.makeRequest('pcApp', planId);
    }).catch(() => {
      apiTargetedCampaignGetAllStore.makeRequest('pcApp');
    });
  }

  /**
   * Triggered when the component updates
   */
  componentDidUpdate() {
    const banners = this.getBanners();
    const isLoading = this.getIsLoading();

    if (this.prevIsLoading && !isLoading && banners.length > 0) {
      const video = this.videoRefs[0];
      if (video) {
        video.play();
      }
    }

    const activeIndexChanged = this.prevActiveIndex !== this.activeIndex;
    const bannersChanged = this.prevBannersLength !== banners.length;

    if (activeIndexChanged || bannersChanged) {
      this.resetImageTimer(banners);
    }

    this.prevIsLoading = isLoading;
    this.prevActiveIndex = this.activeIndex;
    this.prevBannersLength = banners.length;
  }

  /**
   * Triggered when the component is about to be removed from the page.
   */
  componentWillUnmount() {
    this.clearImageTimer();
  }

  /**
   * Get loading state
   *
   * @returns {boolean}
   */
  getIsLoading() {
    const {apiTargetedCampaignGetAllStore, apiUserGetMeStore} = this.props;
    return apiUserGetMeStore.state === STATE_PRE
      || apiUserGetMeStore.state === STATE_PENDING
      || apiTargetedCampaignGetAllStore.state === STATE_PRE
      || apiTargetedCampaignGetAllStore.state === STATE_PENDING;
  }

  /**
   * Get banners from store
   *
   * @returns {Array}
   */
  getBanners() {
    const {apiTargetedCampaignGetAllStore} = this.props;
    const {state, data} = apiTargetedCampaignGetAllStore;
    return state === STATE_FULFILLED
      ? [].concat(...toJS(data || []).map((campaign) => campaign.banners))
      : [];
  }

  /**
   * Clear image timer
   */
  clearImageTimer() {
    if (this.imageTimer) {
      clearTimeout(this.imageTimer);
      this.imageTimer = null;
    }
  }

  /**
   * Reset image timer
   *
   * @param {Array} banners
   */
  resetImageTimer(banners) {
    this.clearImageTimer();
    const banner = banners[this.activeIndex];
    if (banner && !banner.file.mimeType.startsWith('video')) {
      this.imageTimer = setTimeout(() => {
        if (this.isHoveredRef) {
          this.pendingAdvanceRef = true;
        } else {
          this.advance();
        }
      }, DEFAULT_INTERVAL_IN_MILLISECONDS);
    }
  }

  @action advance = () => {
    const banners = this.getBanners();
    this.activeIndex = (this.activeIndex + 1) % banners.length;
  }

  /**
   * @param {number} index
   */
  @action handleSelect = (index) => {
    this.activeIndex = index;
  }

  /**
   * @param {number} index
   */
  handleSlid = (index) => {
    this.videoRefs.forEach((video, i) => {
      if (!video) {
        return;
      }
      if (i === index) {
        video.currentTime = 0;
        video.play();
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  /**
   * Handle video element playback end
   *
   * @param {number} index
   */
  handleVideoEnded = (index) => {
    if (this.isHoveredRef) {
      const video = this.videoRefs[index];
      if (video) {
        video.currentTime = 0;
        video.play();
      }
    } else {
      this.advance();
    }
  }

  handleMouseEnter = () => {
    this.isHoveredRef = true;
  }

  handleMouseLeave = () => {
    this.isHoveredRef = false;
    if (this.pendingAdvanceRef) {
      this.pendingAdvanceRef = false;
      this.advance();
    }
  }

  /**
   * Render banner media
   * @param {{}} banner
   * @param {number} index
   * @returns {{}}
   */
  renderBannerMedia(banner, index) {
    if (banner.file.mimeType.startsWith('video')) {
      return (
        <video
          ref={(el) => {
            this.videoRefs[index] = el;
          }}
          className="w-100 h-100"
          style={{objectFit: 'contain', maxHeight: 480}}
          src={banner.file.url}
          muted
          playsInline
          onEnded={() => this.handleVideoEnded(index)}
        />
      );
    }

    return (
      <img
        className="w-100 h-100"
        style={{objectFit: 'contain', maxHeight: 480}}
        src={banner.file.url}
        alt={`Campaign banner ${banner.id}`}
      />
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {activeIndex} = this;
    const isLoading = this.getIsLoading();
    const banners = this.getBanners();

    return (
      <div
        className="campaign-banner bg-dark w-100 position-relative"
        style={{maxHeight: 480}}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {isLoading && (
          <div className="position-relative">
            <LoadingIcon />
          </div>
        )}

        {!isLoading && banners.length === 0 && (
          <div
            style={{height: '320px'}}
            className="d-flex align-items-center justify-content-center"
          >
            <img
              className="position-relative"
              style={{width: 72, height: 79}}
              src={pcLogo}
              alt="Logo"
            />
          </div>
        )}

        {!isLoading && banners[activeIndex] && banners[activeIndex].ctaUrl && (
          <a
            href={banners[activeIndex].ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="external-link-icon"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        )}

        {!isLoading && banners.length > 0 && (
          <Carousel
            className="w-100"
            controls={banners.length > 1}
            indicators={banners.length > 1}
            activeIndex={activeIndex}
            onSelect={this.handleSelect}
            onSlid={this.handleSlid}
            interval={null}
          >
            {banners.map((banner, index) => (
              <Carousel.Item key={banner.id} className="w-100">
                {banner.ctaUrl ? (
                  <a href={banner.ctaUrl} target="_blank" rel="noopener noreferrer">
                    {this.renderBannerMedia(banner, index)}
                  </a>
                ) : (
                  this.renderBannerMedia(banner, index)
                )}
              </Carousel.Item>
            ))}
          </Carousel>
        )}
      </div>
    );
  }
}

CampaignBanner.propTypes = {
  apiTargetedCampaignGetAllStore: MobxPropTypes.observableObject,
  apiUserGetMeStore: MobxPropTypes.observableObject,
};

CampaignBanner.wrappedComponent = {};
CampaignBanner.wrappedComponent.propTypes = {
  apiTargetedCampaignGetAllStore: MobxPropTypes.observableObject.isRequired,
  apiUserGetMeStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(CampaignBanner)(
  observer(CampaignBanner)
);
